import React, { useMemo } from 'react';

import { Asset, Image } from 'apis/asset-repository-service/types';

export type ImageGalleryContextProps = {
  selectedImages: Image[];
  isAnyImageSelected?: boolean;
  toggleSelection: (toggledImage: Image) => void;
  deselectImageById: (imageId: Asset['id']) => void;
  deselectAll: () => void;
};

const ImageGalleryContext = React.createContext<ImageGalleryContextProps | null>(null);

export const useImageGalleryContext = () => {
  const imageGallery = React.useContext(ImageGalleryContext);

  if (!imageGallery) {
    throw new Error('useImageGalleryContext must be used within ImageGalleryContextProvider');
  }

  return imageGallery;
};

export const ImageGalleryContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedImages, setSelectedImages] = React.useState<Image[]>([]);

  const isAnyImageSelected = useMemo(() => {
    return selectedImages.length > 0;
  }, [selectedImages.length]);

  const toggleSelection = (toggledImage: Image) => {
    setSelectedImages(prev =>
      prev.indexOf(toggledImage) !== -1 ? prev.filter(image => image !== toggledImage) : [...prev, toggledImage],
    );
  };

  const deselectImageById = (imageId: Asset['id']) => {
    setSelectedImages(prev => prev.filter(image => image.id !== imageId));
  };

  const deselectAll = () => {
    setSelectedImages([]);
  };

  return (
    <ImageGalleryContext.Provider
      value={{
        selectedImages,
        isAnyImageSelected,
        toggleSelection,
        deselectImageById,
        deselectAll,
      }}
    >
      {children}
    </ImageGalleryContext.Provider>
  );
};
